// STEP PRODUCT-DETAIL-SIZE-TABLE-1 · 소비자 상품 상세페이지 사이즈 안내.
// 데이터 SoT: seller_products.size_table (OVERLAY-SIZE-TABLE-1 에서 저장/정규화).
// 신규 API/collection 없음 — `GET /products/{id}` 응답에 이미 포함된 size_table 필드 REUSE.
// 셀/guide 는 일반 text 로만 렌더 (React 가 자동 escape 처리).
//
// 표시 조건:
//   · columns / rows / guide_text 중 하나라도 유효한 값이 있으면 렌더
//   · 전부 비어있거나 size_table 자체가 없으면 null (기존 상품 100% 동일 표시 보장)
import React from 'react';

const _normRows = (rows, cols) => {
  if (!Array.isArray(rows)) return [];
  const cap = Math.max(1, (cols || []).length);
  return rows
    .filter((r) => Array.isArray(r))
    .map((r) => {
      const out = [];
      for (let i = 0; i < cap; i += 1) out.push(r[i] == null ? '' : String(r[i]));
      return out;
    })
    .filter((r) => r.some((c) => (c || '').toString().trim() !== ''));
};

export default function ProductSizeTable({ sizeTable }) {
  if (!sizeTable || typeof sizeTable !== 'object') return null;
  const columns = Array.isArray(sizeTable.columns)
    ? sizeTable.columns.map((c) => (c == null ? '' : String(c))).filter((c) => c.trim() !== '')
    : [];
  const rows = _normRows(sizeTable.rows, columns);
  const guide = typeof sizeTable.guide_text === 'string'
    ? sizeTable.guide_text.trim()
    : '';

  if (columns.length === 0 && rows.length === 0 && guide === '') return null;

  const hasTable = columns.length > 0 && rows.length > 0;

  return (
    <section className="mt-10" data-testid="product-size-table-section">
      <h2 className="text-sm font-black tracking-widest uppercase text-gray-500 mb-3">
        사이즈 안내
      </h2>
      <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#0F0F0F]">
        {hasTable && (
          // mobile: 가로 스크롤 허용 (전체 페이지 width 를 깨뜨리지 않음).
          <div className="overflow-x-auto" data-testid="product-size-table-scroll">
            <table className="min-w-full text-xs sm:text-sm" data-testid="product-size-table-grid">
              <thead className="bg-black/[0.03] dark:bg-white/[0.04]">
                <tr>
                  {columns.map((c, ci) => (
                    <th
                      key={ci}
                      scope="col"
                      className="px-3 py-2 border-b border-black/10 dark:border-white/10 font-black whitespace-nowrap text-center"
                      data-testid={`product-size-table-col-${ci}`}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, ri) => (
                  <tr
                    key={ri}
                    className="border-t border-black/5 dark:border-white/5"
                    data-testid={`product-size-table-row-${ri}`}
                  >
                    {columns.map((_, ci) => (
                      <td
                        key={ci}
                        className={`px-3 py-2 text-center whitespace-nowrap ${
                          ci === 0 ? 'font-bold text-black dark:text-white' : 'text-gray-700 dark:text-gray-300'
                        }`}
                        data-testid={`product-size-table-cell-${ri}-${ci}`}
                      >
                        {r[ci] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {guide && (
          <p
            className="px-3 py-3 text-xs sm:text-sm whitespace-pre-line text-gray-600 dark:text-gray-400 border-t border-black/5 dark:border-white/5"
            data-testid="product-size-table-guide"
          >
            {guide}
          </p>
        )}
      </div>
      <p className="mt-2 text-[11px] text-gray-400">
        측정 방법에 따라 1~2cm 오차가 있을 수 있습니다.
      </p>
    </section>
  );
}
